import React from 'react'

import { useTableSort } from '../../components/Table'
import { ExternalLink } from '../../components/external-link'
import { FinancialsWidget, getMoMPercentageData, useFinancialsTableContext } from '../../components/financials-table'
import { LineStackedBarChart } from '../../components/line-stacked-bar-chart'
import { TrafficTableBottomContent } from './TrafficTableBottomContent'
import { TrafficTableHead } from './TrafficTableHead'
import { WidgetDomainSettingsSection } from './WidgetDomainSettingsSection'
import { SEGMENT_COLORS } from './constants'
import { TrafficRowDataType, TrafficSettings, TrafficSubRowDataType } from './types'
import { ParsedTrafficItem } from './types'
import { useMonthlyDataControls } from './useMonthlyDataControls'
import { useTableColumns } from './useTableColumns'
import { getDomainHref } from './utils'

interface MapDataProps {
  mainTraffic: ParsedTrafficItem
  peerTraffics: ParsedTrafficItem[]
  industryTraffics: ParsedTrafficItem[]
  isPlaceholder: boolean
}

interface Props extends Omit<MapDataProps, 'isPlaceholder'> {
  hasBeenInView: boolean
  isInView: boolean
}

function mapData({
  mainTraffic,
  peerTraffics,
  industryTraffics,
  yearKeys,
  isPlaceholder,
}: MapDataProps & { yearKeys: number[] }): TrafficRowDataType[] {
  const allTraffics = [mainTraffic, ...peerTraffics, ...industryTraffics]
  const domainsArray = allTraffics.map((traffic, index) => {
    const values = !isPlaceholder
      ? yearKeys.map((date) => {
          const index = traffic.traffic?.d.findIndex((d) => d === date) ?? -1
          return {
            year: date.toString(),
            value: traffic.traffic?.v[index] ?? null,
          }
        })
      : []

    const valuesWithMomData: TrafficSubRowDataType['values'] = []
    const keyValueMap: TrafficSubRowDataType['keyValueMap'] = {}
    const momKeyValueMap: TrafficSubRowDataType['momKeyValueMap'] = {}

    if (!isPlaceholder) {
      const momData = getMoMPercentageData(values)
      values.forEach(({ year, value }) => {
        const momValue = momData[year] ?? null
        keyValueMap[year] = value
        momKeyValueMap[year] = momValue

        valuesWithMomData.push({
          year,
          value,
          momValue,
        })
      })
    }

    const isHighlighted = traffic.domain === mainTraffic.domain

    return {
      label: traffic.domain!,
      id: traffic.domain!,
      labelElement: <ExternalLink label={traffic.domain!} href={getDomainHref(traffic)} isBold={isHighlighted} />,
      labelColumnClassName: 'whitespace-nowrap',
      isLabelColumnSticky: true,
      color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
      isHighlighted,
      isSelected: traffic.domain === mainTraffic.domain || !!mainTraffic?.peers?.includes(traffic.domain!),
      values: valuesWithMomData,
      keyValueMap,
      momKeyValueMap,
    }
  })

  return domainsArray
}

export function Domain({
  mainTraffic,
  peerTraffics,
  industryTraffics: industryTrafficsRaw,
  hasBeenInView,
  isInView,
}: Props) {
  const { settings } = useFinancialsTableContext<TrafficSettings>()
  const clearInactiveIdsBlacklist = React.useMemo<string[]>(
    () => (mainTraffic ? [mainTraffic, ...peerTraffics].map(({ domain }) => domain ?? '') : ([] as string[])),
    [mainTraffic, peerTraffics]
  )
  const { monthlyDataRowSelectionOverrides, toggleMonthlyDataRowSelection, isShowMore, toggleIsShowMore } =
    useMonthlyDataControls({
      clearInactiveIdsBlacklist,
    })
  const industryTraffics = React.useMemo(() => {
    if (isShowMore) {
      return industryTrafficsRaw
    }
    return industryTrafficsRaw.filter(({ domain }) => domain && monthlyDataRowSelectionOverrides[domain] !== undefined)
  }, [industryTrafficsRaw, monthlyDataRowSelectionOverrides, isShowMore])

  const yearKeys = React.useMemo(() => {
    const allTraffics = [mainTraffic, ...peerTraffics, ...industryTraffics]
    return allTraffics
      .flatMap(({ traffic }) => traffic?.d)
      .filter((year, index, arr) => year !== undefined && arr.indexOf(year) === index)
      .sort((a, b) => a! - b!) as number[]
  }, [mainTraffic, peerTraffics, industryTraffics])
  const stringYearKeys = React.useMemo(() => yearKeys.map((y) => y.toString()), [yearKeys])
  const [monthlyData, setMonthlyData] = React.useState(() =>
    mapData({ peerTraffics, mainTraffic, industryTraffics, yearKeys, isPlaceholder: true })
  )
  const { columns, valueGetter } = useTableColumns(stringYearKeys, settings.trafficDataView, hasBeenInView)

  const {
    data: sortedData,
    sort,
    changeSort,
  } = useTableSort({
    data: monthlyData,
    columns,
    defaultSort: { column: columns[columns.length - 1], direction: -1 },
    key: hasBeenInView ? 1 : 0,
    valueGetter,
  })

  React.useEffect(() => {
    if (!hasBeenInView) {
      return
    }

    const newMonthlyData = mapData({
      peerTraffics,
      mainTraffic,
      industryTraffics,
      yearKeys,
      isPlaceholder: false,
    })
    setMonthlyData(
      newMonthlyData.map((data, index) => ({
        ...data,
        isSelected: monthlyDataRowSelectionOverrides[data.id] ?? index < [mainTraffic, ...peerTraffics].length,
      }))
    )
  }, [
    peerTraffics,
    mainTraffic,
    industryTraffics,
    yearKeys,
    monthlyDataRowSelectionOverrides,
    hasBeenInView,
    settings.trafficDataView,
  ])

  return (
    <div className="fv-container px-2.5 pb-8 pt-4 xl:px-5">
      <FinancialsWidget
        data={sortedData}
        header={<WidgetDomainSettingsSection />}
        tableHead={<TrafficTableHead columns={columns} sort={sort} onSortChange={changeSort} />}
        columns={columns}
        hasStickyColumnWrapper={hasBeenInView}
        isTableFixed={false}
        onVisibilityChange={toggleMonthlyDataRowSelection}
      >
        <LineStackedBarChart
          data={sortedData}
          yearKeys={stringYearKeys}
          chartView={settings.chartView}
          lineChartTooltipSort="value"
          isInView={isInView}
          hasBeenInView={hasBeenInView}
          isFullWidth
          hasXAxis
        />
      </FinancialsWidget>
      <TrafficTableBottomContent
        showMoreLabel="Expand List of Similar Domains"
        showLessLabel="Collapse List of Similar Domains"
        hasHiddenRows={industryTrafficsRaw.length > 0}
        isShowMore={isShowMore}
        onShowMoreToggle={toggleIsShowMore}
      />
    </div>
  )
}
